import CloseIcon from '@mui/icons-material/Close'
import LoadingButton from '@mui/lab/LoadingButton'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material'
import { ReactElement, ReactNode, useEffect, useState } from 'react'

interface IModalConfig {
    title: string
    children?: ReactElement
    labels?: { confirm: string, cancel: string }
    colors?: { confirm: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning', cancel: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' }
    startIcon?: ReactNode
    onCancel?: (() => void) | (() => Promise<void>)
    onConfirm?: (() => void) | (() => Promise<void>)
}

interface IModals {
    openConfirm: (options: IModalConfig) => void
}

export const modals: IModals = {
    openConfirm: null
}

function ModalService() {
    const [open, setOpen] = useState(false)
    const [config, setConfig] = useState<IModalConfig>(null)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        modals.openConfirm = ({ title, children, labels, colors, startIcon, onCancel, onConfirm }) => {
            setConfig({ title, children, labels, colors, startIcon, onCancel, onConfirm })
            setOpen(true)
        }
    }, [])

    const close = () => {
        setOpen(false)
        setConfig(null)
    }

    return (
        <Dialog open={open} onClose={close}>
            <DialogTitle display='flex' justifyContent='space-between'>
                <Typography variant='h6'>{config?.title}</Typography>
                <IconButton
                    size='small'
                    onClick={close}>
                    <CloseIcon fontSize='small' />
                </IconButton>

            </DialogTitle>
            <DialogContent>
                {config?.children}
            </DialogContent>
            {(config?.onCancel || config?.onConfirm) && (
                <DialogActions>
                    <Button
                        disabled={loading}
                        size='small'
                        color={config?.colors?.cancel}
                        variant='outlined'
                        onClick={async () => {
                            if (config?.onCancel) await config.onCancel()
                            setOpen(false)
                        }}>
                        {config?.labels?.cancel ?? 'Cancel'}
                    </Button>
                    <LoadingButton
                        startIcon={config?.startIcon ?? undefined}
                        loadingPosition={config?.startIcon ? 'start' : undefined}
                        size='small'
                        color={config?.colors?.confirm}
                        autoFocus
                        variant='contained'
                        loading={loading}
                        onClick={async () => {
                            if (config?.onConfirm) {
                                setLoading(true)
                                await config.onConfirm()
                                setOpen(false)
                                setLoading(false)
                            }
                        }}>
                        {config?.labels?.confirm ?? 'Confirm'}
                    </LoadingButton>
                </DialogActions>
            )}
        </Dialog>
    )
}

export default ModalService
